import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Empty, Table, Td, Badge } from "../components/UI";
import { SlidePanel } from "../components/SlidePanel";
import { FLabel, FInput } from "./engagement/FormControls";
import { ArrowLeft, Users, CalendarDays, CheckCircle2, Percent } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

export default function Group360() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [sess, setSess] = useState({ date: dayjs().format("YYYY-MM-DD"), start_time: "09:00", end_time: "10:30" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/groups/${id}/detail360`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="py-16 text-center text-sm text-[#84675B]">Loading group 360…</div>;
  const { group: g, stats } = d;
  const addSession = async () => {
    setBusy(true);
    try {
      await api.post("/groups", { name: g.name, group_type: g.group_type, curriculum: g.curriculum, program: g.program,
        cpt_code: g.cpt_code, location: g.location, location_type: g.location_type, room: g.room,
        facilitator: g.facilitator, co_facilitator: g.co_facilitator, clients: g.clients || [],
        objective: g.objective, intervention: g.intervention, plan: g.plan, status: "scheduled", ...sess });
      toast.success("New session added to this group"); setAddOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const cards = [
    { icon: CalendarDays, label: "Total sessions", value: stats.total_sessions },
    { icon: CheckCircle2, label: "Completed", value: stats.completed },
    { icon: Percent, label: "Attendance rate", value: `${stats.attendance_rate}%` },
    { icon: Users, label: "Members", value: stats.member_count },
  ];
  return (
    <div data-testid="group-360-page">
      <button data-testid="group-360-back" onClick={() => navigate("/groups")} className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-[#7B6157] hover:text-[#4E342E]"><ArrowLeft size={15} /> Back to Groups</button>
      <PageHeader title={g.name} subtitle={`${g.group_type || "Group"} · Facilitator: ${g.facilitator || g.leader || "-"} · ${g.program || ""} ${g.cpt_code ? `· CPT ${g.cpt_code}` : ""}`}
        action={<Btn data-testid="g360-add-session-btn" onClick={() => setAddOpen(true)}><CalendarDays size={15} /> Add Session</Btn>} />
      <div className="mb-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="p-4 flex items-center gap-3" data-testid={`g360-stat-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F5F2EB] text-[#7B6157]"><c.icon size={18} /></span>
            <div><div className="font-heading text-2xl font-bold text-[#3E2723]">{c.value}</div>
              <div className="text-[11px] uppercase font-semibold text-[#84675B]">{c.label}</div></div>
          </Card>
        ))}
      </div>
      <div className="grid gap-5 xl:grid-cols-2">
        <Card className="p-5" data-testid="g360-members">
          <h3 className="mb-3 font-heading text-sm font-bold text-[#3E2723]">Member roster & attendance</h3>
          {d.members.length === 0 ? <Empty text="No members recorded yet." /> : (
            <Table headers={["Client", "MRN", "Sessions", "Attended", "Rate"]}>
              {d.members.map((m) => (
                <tr key={m.client_id} className="border-t border-[#F1EEE7] hover:bg-[#FDFCF8] cursor-pointer" onClick={() => navigate(`/clients/${m.client_id}/console`)} data-testid={`g360-member-${m.client_id}`}>
                  <Td className="font-medium">{m.name}</Td><Td>{m.mrn}</Td><Td>{m.sessions}</Td><Td>{m.attended}</Td>
                  <Td><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${m.rate >= 75 ? "bg-[#E8F5E9] text-[#2E7D32]" : m.rate >= 50 ? "bg-[#FFF8E1] text-[#8D6E00]" : "bg-[#FFEBEE] text-[#C62828]"}`}>{m.rate}%</span></Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
        <Card className="p-5" data-testid="g360-chart">
          <h3 className="mb-3 font-heading text-sm font-bold text-[#3E2723]">Sessions by month</h3>
          <div className="h-60">
            <ResponsiveContainer>
              <BarChart data={d.sessions_by_month}><CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip /><Bar dataKey="count" fill="#4E342E" radius={[5, 5, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          </div>
          {(g.objective || g.intervention || g.plan) && (
            <div className="mt-4 space-y-2 text-sm">
              {g.objective && <div><span className="font-semibold text-[#3E2723]">Objective:</span> <span className="text-[#5D4037]">{g.objective}</span></div>}
              {g.intervention && <div><span className="font-semibold text-[#3E2723]">Intervention:</span> <span className="text-[#5D4037]">{g.intervention}</span></div>}
              {g.plan && <div><span className="font-semibold text-[#3E2723]">Plan:</span> <span className="text-[#5D4037]">{g.plan}</span></div>}
            </div>
          )}
        </Card>
      </div>
      <Card className="mt-5 overflow-hidden" data-testid="g360-sessions">
        <div className="px-5 pt-4 font-heading text-sm font-bold text-[#3E2723]">Session history</div>
        <Table headers={["Date", "Time", "Location", "Facilitator", "Attendance", "Status"]}>
          {d.sessions.map((s) => (
            <tr key={s.id} className="border-t border-[#F1EEE7]">
              <Td>{dayjs(s.date).format("MM/DD/YYYY")}</Td>
              <Td>{s.start_time}{s.end_time ? ` to ${s.end_time}` : ""}</Td>
              <Td>{s.location || s.room || "-"}</Td>
              <Td>{s.facilitator || s.leader || "-"}</Td>
              <Td>{Object.keys(s.attendance || {}).length ? `${Object.values(s.attendance).filter((v) => ["S", "M", "C"].includes(v)).length}/${Object.keys(s.attendance).length} present` : "-"}</Td>
              <Td><Badge value={s.status} /></Td>
            </tr>
          ))}
        </Table>
      </Card>
      <SlidePanel open={addOpen} onClose={() => setAddOpen(false)} title="Add Group Session" subtitle={`New session of "${g.name}" with the current roster`} testId="g360-add-session-panel">
        <div className="space-y-4">
          <div><FLabel>Date</FLabel><FInput data-testid="g360-session-date" type="date" value={sess.date} onChange={(e) => setSess((s) => ({ ...s, date: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Start</FLabel><FInput data-testid="g360-session-start" type="time" value={sess.start_time} onChange={(e) => setSess((s) => ({ ...s, start_time: e.target.value }))} /></div>
            <div><FLabel>End</FLabel><FInput data-testid="g360-session-end" type="time" value={sess.end_time} onChange={(e) => setSess((s) => ({ ...s, end_time: e.target.value }))} /></div>
          </div>
          <p className="text-xs text-[#7B6157]">Roster ({(g.clients || []).length} members), facilitator, program & CPT are copied from this group.</p>
          <div className="flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => setAddOpen(false)}>Cancel</Btn>
            <Btn data-testid="g360-session-save-btn" onClick={addSession} disabled={busy}>{busy ? "Adding…" : "Add Session"}</Btn>
          </div>
        </div>
      </SlidePanel>
    </div>
  );
}
